import { cn } from '@/shared/utils';
import { Button } from '@/view/shared/Button';
import { type TrialBannerTone, useTrialBannerViewModel } from '@/viewmodel/features/quota';
import { Clock, Lock, X } from 'lucide-react';
import type { ReactNode } from 'react';

const TONE_CLASS: Record<TrialBannerTone, { box: string; icon: string }> = {
  info: { box: 'border-primary/30 bg-primary/5', icon: 'text-primary' },
  warning: { box: 'border-warning/40 bg-warning/5', icon: 'text-warning' },
  danger: { box: 'border-destructive/40 bg-destructive/5', icon: 'text-destructive' },
};

export function TrialBanner(): ReactNode {
  const banner = useTrialBannerViewModel();

  if (!banner) return null;
  const tone = TONE_CLASS[banner.tone];
  const Icon = banner.expired ? Lock : Clock;

  return (
    <div
      role={banner.expired ? 'alert' : 'status'}
      data-testid="trial-banner"
      className={cn(
        'relative shrink-0 flex flex-col gap-2 border-b px-4 py-3 pr-10 sm:pr-6 sm:flex-row sm:items-center sm:px-6 md:px-8 lg:px-16',
        tone.box,
      )}
    >
      <Icon size={16} className={cn('shrink-0', tone.icon)} />
      <div className="flex flex-1 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
        <p className="text-xs font-semibold text-foreground">{banner.title}</p>
        <p className="text-xs text-muted-foreground">{banner.text}</p>
      </div>
      <Button
        variant={banner.expired ? 'primary' : 'secondary'}
        size="sm"
        onClick={banner.onAction}
        className="shrink-0"
      >
        {banner.actionLabel}
      </Button>
      {banner.onDismiss && (
        <button
          type="button"
          aria-label={banner.dismissLabel}
          onClick={banner.onDismiss}
          className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-surface-secondary hover:text-foreground sm:static sm:ml-1"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
